import { clsx } from "clsx";
import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import type { NextStepCardSchema } from "@/schemas/sections";
import { ArrowIcon } from "./icons";
import { DEFAULT_EMOJI, DEFAULT_TONE_ORDER, TONE_STYLES } from "./tone-tokens";

export function NextStepCardLink({
  card,
  index,
}: {
  card: z.input<typeof NextStepCardSchema>;
  index: number;
}) {
  const external = card.external ?? /^https?:\/\//.test(card.href);
  const tone = TONE_STYLES[card.tone ?? DEFAULT_TONE_ORDER[index % DEFAULT_TONE_ORDER.length]];
  return (
    <a
      href={card.href}
      data-track={`lm_next.cta.${card.kind}`}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={clsx(
        "group flex flex-row items-center gap-3 rounded-2xl p-4 text-left no-underline shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl md:flex-col md:items-stretch md:gap-0 md:rounded-3xl md:p-6 md:hover:-translate-y-1",
        tone.card,
      )}
    >
      <Flex
        as="span"
        aria-hidden="true"
        align="center"
        justify="center"
        className={clsx(
          "h-11 w-11 shrink-0 rounded-xl text-(length:--type-heading) md:mb-3",
          tone.chip,
        )}
      >
        {card.emoji ?? DEFAULT_EMOJI[card.kind]}
      </Flex>
      <div className="min-w-0 flex-1">
        <Flex align="center" justify="between" gap={8}>
          <p className={clsx("font-(weight:--fw-bold)", tone.title)}>{card.title}</p>
          {card.badge && (
            <span
              className={clsx(
                "hidden shrink-0 rounded-full px-2.5 py-1 text-(length:--type-caption) font-(weight:--fw-bold) md:inline-block",
                tone.badge,
              )}
            >
              {card.badge}
            </span>
          )}
        </Flex>
        <p
          className={clsx(
            "mt-0.5 text-(length:--type-body-sm) leading-(--lh-body) md:mt-1 md:leading-(--lh-relaxed)",
            tone.body,
          )}
        >
          {card.description}
        </p>
        <span
          className={clsx(
            "mt-4 hidden items-center gap-2 font-(weight:--fw-bold) md:inline-flex",
            tone.cta,
          )}
        >
          {card.cta}
          <ArrowIcon />
        </span>
      </div>
      <span className={clsx("shrink-0 md:hidden", tone.cta)}>
        <ArrowIcon />
      </span>
    </a>
  );
}
